import type { Translations } from "./pt";

export const en: Translations = {
  language: {
    label: "Language",
    pt: "Português",
    en: "English",
    es: "Español",
  },
  theme: {
    label: "Theme",
    light: "Light",
    dark: "Dark",
  },
  nav: {
    home: "Home",
    tickets: "Tickets",
    newTicket: "New Ticket",
    service: "Service",
    sla: "SLA",
    faq: "FAQ",
    policy: "Policy",
    catalog: "Catalog",
    docs: "Documentation",
    admin: "Admin",
    reports: "Reports",
    queues: "Queues",
    changes: "Changes",
    approvals: "Approvals",
    problems: "Problems",
    menu: "Menu",
    menuTitle: "Navigation",
    groupService: "Service",
    groupManagement: "Management",
    groupKnowledge: "Knowledge",
  },
  common: {
    login: "Login",
    logout: "Log out",
    logoutSuccess: "Logged out",
    logoutDescription: "You have been successfully logged out.",
  },
  roles: {
    admin: "Internal Admin",
    internal: "Administrator",
    analyst: "Analyst",
    external: "User",
  },
  user: {
    myTickets: "My Tickets",
    servicePanel: "Service Panel",
    adminDashboard: "Admin Dashboard",
    serviceCatalog: "Service Catalog",
    editProfile: "Edit profile",
  },
  profile: {
    title: "Edit profile",
    name: "Name",
    email: "Email",
    emailReadOnly: "Email cannot be changed.",
    avatar: "Profile photo",
    avatarChange: "Change photo",
    avatarUpload: "Upload photo",
    avatarCrop: "Crop photo",
    avatarCropHint: "Adjust the photo area. Drag to reposition and use the zoom.",
    avatarCropConfirm: "Use this photo",
    avatarCropCancel: "Cancel",
    changePassword: "Change password",
    sendCode: "Send code by email",
    codeSent: "Code sent. Check your email (valid for 15 min).",
    code: "Code",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    save: "Save",
    saveName: "Save name",
    changePasswordSubmit: "Change password",
    successName: "Name updated.",
    successAvatar: "Photo updated.",
    successPassword: "Password changed successfully.",
    errorCode: "Invalid or expired code.",
    errorPasswordMatch: "Passwords do not match.",
    errorPasswordLength: "Password must be at least 6 characters.",
  },
  notifications: {
    title: "Notifications",
    markAllAsRead: "Mark all as read",
    none: "No notifications",
    markAllAsReadToast: "Notifications marked as read",
    markAllAsReadDesc: "All notifications have been marked as read.",
  },
  trial: {
    evaluation: "Trial period",
    ended: "Trial ended",
    daysLeft: (n: number) =>
      `You have ${n} day${n === 1 ? "" : "s"} left in your trial with unlimited tickets.`,
    contact:
      "Your trial has ended. Contact us to enable billing and continue using the system.",
    label: (n: number) =>
      `(Trial: ${n} day${n === 1 ? "" : "s"} left)`,
    labelEnded: "(Trial ended)",
  },
  pageTitle: {
    default: "TEC-IT ITSM - Ticket Management System",
  },
  businessHours: {
    badgeHuman: "Human Support",
    badgeOff: "Outside Hours",
    messageUntil: (time: string) => `Human support available until ${time}`,
    messageBot: (time: string) => `Bot support. Human support returns at ${time}`,
  },
  dashboard: {
    loading: "Loading tickets...",
    title: "Service Panel",
    subtitle: "Complete ticket management - Internal View",
    refresh: "Refresh",
    total: "Total",
    open: "Open",
    inProgress: "In Progress",
    resolved: "Resolved",
    slaExpired: "SLA Expired",
    unassigned: "Unassigned",
    critical: "Critical",
    queueN1: "Queue N1 - First Level",
    queueN2: "Queue N2 - Second Level",
    queueN3: "Queue N3 - Specialized",
    activeTickets: "Active tickets",
    byCategory: "Tickets by Category",
    byTechnician: "Tickets by Technician",
    noData: "No data available",
    qualityMetrics: "Quality Metrics",
    npsAverage: "Average NPS",
    reopenRate: "Reopen Rate",
    reopenedCount: (n: number) => `${n} tickets reopened`,
    transfersEscalations: "Transfers and Escalations",
    totalTransfers: "Total Transfers",
    ticketsTransferred: "Tickets Transferred",
    multipleTransfers: (n: number) => `${n} with multiple transfers`,
    avgPerTicket: "Avg per Ticket",
    filtersTitle: "Filters and Advanced Search",
    searchPlaceholder: "Search...",
    status: "Status",
    statusActive: "In progress",
    statusAll: "All Statuses",
    statusOpen: "Open",
    statusInProgress: "In Progress",
    statusWaiting: "Waiting for Response",
    statusResolved: "Resolved",
    statusClosed: "Closed",
    priority: "Priority",
    priorityAll: "All Priorities",
    priorityCritical: "Critical",
    priorityHigh: "High",
    priorityMedium: "Medium",
    priorityLow: "Low",
    type: "Type",
    typeAll: "All Types",
    typeIncident: "Incident",
    typeRequest: "Request",
    typeProblem: "Problem",
    typeChange: "Change",
    queue: "Queue",
    queueAll: "All Queues",
    queueN1Short: "N1 - First Level",
    queueN2Short: "N2 - Second Level",
    queueN3Short: "N3 - Specialized",
    company: "Company",
    companyAll: "All Companies",
    assignee: "Assignee",
    assigneeAll: "All",
    assigneeUnassigned: "Unassigned",
    sla: "SLA",
    slaAll: "All",
    slaExpiredFilter: "SLA Expired",
    slaWarning: "SLA Soon",
    clearFilters: "Clear Filters",
    ticketsList: "Tickets",
    ticketsListDesc: "Complete list of tickets with detailed information",
    noCompany: "No company",
    noRequester: "No requester",
    category: "Category",
    created: "Created",
    assigneeLabel: "Assignee",
    notAssigned: "Not assigned",
    noTicketsFound: "No tickets found",
    adjustFilters: "Adjust filters to find tickets",
    linkedReported: (n: number) => (n === 1 ? "1 person reported the same" : `${n} people reported the same`),
  },
  index: {
    serviceCenter: "Service Center",
    heroSubtitle: "Complete technical and operational ticket management system.",
    heroSubtitle2: "Open, track and resolve your tickets with agility and transparency.",
    accessSystem: "Access System",
    openTicket: "Open Ticket",
    ticketSystem: "Ticket System",
    ticketSystemDesc: "Automated ITIL-based categorization and prioritization system",
    support24: "24/7 Support",
    support24Desc: "Real-time communication with our specialized technical team",
    slaTransparent: "Transparent SLA",
    slaTransparentDesc: "Track progress with visual resolution time indicators",
    totalSecurity: "Total Security",
    totalSecurityDesc: "Secure environment with access control and full history",
    mainResources: "Main Resources",
    mainResourcesDesc: "Everything you need to manage your tickets efficiently",
    fullManagement: "Complete Management",
    fullManagementDesc: "Incidents, Requests, Problems and Changes in a single integrated platform",
    intuitiveDashboard: "Intuitive Dashboard",
    intuitiveDashboardDesc: "View KPIs, performance metrics and real-time status with interactive charts",
    mobileFirst: "Mobile First",
    mobileFirstDesc: "Responsive interface optimized for all devices, access from anywhere",
  },
  auth: {
    backToHome: "Back to home",
    backToLogin: "Back to login",
    accessAccount: "Access your account",
    loginTitle: "Login",
    loginDesc: "Enter your credentials to access the ticket system",
    email: "Email",
    emailPlaceholder: "your@email.com",
    password: "Password",
    forgotPassword: "Forgot my password",
    forgotPasswordAlt: "Forgot your password?",
    rememberMe: "Remember me",
    centralDeServicos: "Service Center",
    accessSystem: "Access System",
    openTicket: "Open Ticket",
    registerCta: "Sign up",
    hasAccount: "Already have an account?",
    signInLink: "Sign in",
    tagline: "Complete system for technical and operational ticket management",
    signIn: "Sign in",
    signingIn: "Signing in...",
    noAccount: "Don't have an account?",
    createAccount: "Create account",
    welcomeTitle: "Welcome!",
    welcomeDesc: "Register your company and start using the ticket system.",
    signUpCta: "Sign up",
    detectingCompany: "Detecting company...",
    forcePasswordReset: "Password change required",
    setNewPassword: "Set a new password to continue.",
    loginSuccess: "Login successful!",
    redirecting: "Redirecting to dashboard...",
    loginError: "Login error",
    invalidCredentials: "Invalid credentials",
    connectionError: "Could not connect to server",
    checkEmail: "Check your email",
    checkEmailDesc: "If the email exists, you will receive the link.",
    recoveryError: "Recovery request error",
    recoveryErrorDesc: "Could not process your request.",
    recoverPassword: "Recover password",
    forgotTitle: "Forgot my password",
    forgotDesc: "Enter your email to receive the reset link",
    sendLink: "Send link",
    sending: "Sending...",
    resetPasswordTitle: "Reset password",
    newPassword: "New password",
    newPasswordDesc: "Set your new password to access the system",
    invalidLink: "Invalid or incomplete link. Request a new recovery email.",
    confirmPassword: "Confirm password",
    savePassword: "Reset password",
    saving: "Saving...",
    passwordReset: "Password reset",
    passwordResetDesc: "You can now log in.",
    resetError: "Error resetting password",
    resetErrorDesc: "Could not complete the operation.",
    invalidPassword: "Invalid password",
    passwordMinLength: "Password must be at least 6 characters.",
    passwordsDontMatch: "Passwords do not match",
    confirmPasswordCorrect: "Confirm password correctly.",
  },
  register: {
    title: "Register",
    desc: "Fill in the data to create your account",
    name: "Full name",
    namePlaceholder: "Your full name",
    phone: "Phone",
    phonePlaceholder: "(11) 98765-4321",
    companyDomain: "Company domain",
    companyDomainPlaceholder: "example.com",
    companyDomainHint: "Enter your company domain (no www)",
    sector: "Sector",
    sectorPlaceholder: "E.g. IT, HR, Finance",
    function: "Function",
    functionPlaceholder: "E.g. Analyst, Manager, Coordinator",
    confirmPassword: "Confirm password",
    emailHint: "Use your company email",
    passwordHint: "At least 6 characters",
    creatingAccount: "Creating account...",
    createButton: "Create account",
    error: "Error",
    nameRequired: "Name is required.",
    emailRequired: "Email is required.",
    invalidEmail: "Invalid email.",
    phoneRequired: "Phone is required.",
    domainRequired: "Company domain is required.",
    sectorRequired: "Sector is required.",
    functionRequired: "Function is required.",
    passwordMin: "Password must be at least 6 characters.",
    passwordsDontMatch: "Passwords do not match.",
    registerSuccess: "Account created successfully!",
    registerSuccessDesc: "Redirecting to login...",
    registerSuccessMessage: "You have 20 days of trial with unlimited tickets. After that, billing will be required.",
    registerError: "Registration error",
  },
  landing: {
    loginSuccess: "Login successful!",
    redirecting: "Redirecting to dashboard...",
    loginError: "Login error",
    invalidCredentials: "Invalid credentials",
    connectionError: "Could not connect to server",
    signIn: "Sign in",
    signingIn: "Signing in...",
    accessAccount: "Access your account",
    noAccount: "Don't have an account?",
    createAccount: "Create account",
    forgotPassword: "Forgot my password",
    newAccount: "Create account",
  },
  itsmDashboard: {
    title: "ITSM Dashboard",
    subtitle: "Manage and track your tickets",
    newTicket: "New Ticket",
    loading: "Loading tickets...",
    error: "Error loading tickets",
    connectionError: "Connection error",
    totalTickets: "Total Tickets",
    tryAgain: "Try again",
    searchPlaceholder: "Search by ID or title...",
    filtersTitle: "Filters and Search",
    noTickets: "No tickets found",
    noTicketsDesc: "You have no tickets yet or the filters returned no results.",
    openTicket: "Open Ticket",
  },
  footer: {
    companyDesc: "Professional ticket management system based on ITIL best practices.",
    quickAccess: "Quick Access",
    myTickets: "My Tickets",
    openTicket: "Open Ticket",
    slaLevels: "SLA Levels",
    support: "Support",
    servicePolicy: "Service Policy",
    contact: "Contact",
    available24: "24/7 Available",
    rightsReserved: "All rights reserved.",
  },
  notFound: {
    title: "404",
    message: "Page not found",
    backHome: "Return to Home",
  },
  faq: {
    title: "Help Center",
    subtitle: "Find quick answers to the most common questions about the ITSM system",
    searchPlaceholder: "Search FAQ...",
    noResults: "No results found",
    categoryGeneral: "General",
    categorySla: "SLA and Priorities",
    categoryTypes: "Ticket Types",
    categoryComm: "Communication",
    categoryTech: "Technical Issues",
  },
  sla: {
    title: "SLA Levels",
    subtitle: "Our response and resolution time commitments based on ITIL classification. Deadlines start from ticket opening during business hours (8am to 6pm, Monday to Friday).",
    howItWorks: "How our SLA works",
    responseTime: "Response Time",
    responseTimeDesc: "Maximum time for our team to make first contact and start handling your ticket.",
    resolutionTime: "Resolution Time",
    resolutionTimeDesc: "Maximum time for complete ticket resolution, including tests and user confirmation.",
    serviceHours: "Service Hours",
    weekdays: "Monday to Friday: 08:00 to 18:00",
    emergencies: "Emergencies: 24/7 for critical tickets",
    slaCalculation: "SLA Calculation",
    slaCalculationDesc: "Deadlines are calculated only during business hours, except for critical tickets.",
    response: "Response",
    resolution: "Resolution",
    priority: "Priority",
    description: "Description",
    typeIncident: "Incident",
    typeRequest: "Request",
    typeProblem: "Problem",
    typeChange: "Change",
    typeIncidentDesc: "Unplanned interruption or reduction in quality of a service",
    typeRequestDesc: "User request for information, advice, standard change or access",
    typeProblemDesc: "Unknown cause of one or more existing or potential incidents",
    typeChangeDesc: "Addition, modification or removal of anything that could affect services",
    incidentCriticalDesc: "Main system unavailable",
    incidentHighDesc: "Important functionality affected",
    incidentMediumDesc: "Problem affecting few users",
    incidentLowDesc: "Minor or cosmetic problem",
    requestCriticalDesc: "Urgent access to critical system",
    requestHighDesc: "Important new functionality",
    requestMediumDesc: "Standard request",
    requestLowDesc: "Information or improvements",
    problemCriticalDesc: "Cause of multiple critical incidents",
    problemHighDesc: "Pattern of recurring incidents",
    problemMediumDesc: "Preventive analysis",
    problemLowDesc: "Process improvement",
    changeCriticalDesc: "Emergency change",
    changeHighDesc: "Important planned change",
    changeMediumDesc: "Standard change",
    changeLowDesc: "Non-urgent improvement",
    notesTitle: "Important Notes",
    criticalNote: "Critical Tickets: Receive 24/7 support, including weekends and holidays.",
    communicationNote: "Communication: You will be notified by email on every ticket status change.",
    escalationNote: "Escalation: Tickets exceeding 80% of SLA are automatically escalated to supervision.",
    slaPauseNote: "SLA Pause: The timer is paused when we are waiting for requester response.",
    reopenNote: "Reopening: Tickets can be reopened within 48h after closure without SLA impact.",
  },
  servicePolicy: {
    title: "Service Policy",
    subtitle: "Learn about our service standards, responsibilities and commitments to technical support quality.",
    qualityCommitment: "Quality Commitment",
    introParagraph: "TEC-IT is committed to providing excellent technical support, following ITIL (Information Technology Infrastructure Library) best practices to ensure efficiency, transparency and user satisfaction.",
    availability: "Availability",
    satisfaction: "Satisfaction",
    forCritical: "For Critical",
    scopeTitle: "Service Scope",
    scopeDesc: "What is included in our technical support",
    included: "Included Services",
    excluded: "Excluded Services",
    includedItem1: "IT infrastructure support",
    includedItem2: "Hardware and software maintenance",
    includedItem3: "Systems and network configuration",
    includedItem4: "User and access management",
    includedItem5: "Backup and data recovery",
    includedItem6: "Information security",
    includedItem7: "Technical consulting",
    includedItem8: "Systems monitoring",
    excludedItem1: "Custom software development",
    excludedItem2: "Basic user training",
    excludedItem3: "Personal equipment support",
    excludedItem4: "Non-corporate configurations",
    excludedItem5: "Data recovery due to negligence",
    excludedItem6: "Physical maintenance of old equipment",
    excludedItem7: "Third-party software licensing",
    excludedItem8: "Support for non-approved systems",
    serviceHoursTitle: "Service Hours",
    businessHours: "Business Hours",
    weekdays: "Monday to Friday:",
    weekdaysTime: "08:00 to 18:00",
    saturday: "Saturday:",
    saturdayTime: "08:00 to 12:00",
    sundayHolidays: "Sunday and Holidays:",
    sundayHolidaysTime: "Emergencies only",
    support247Title: "24/7 Support",
    criticalTicketsBadge: "Critical Tickets",
    criticalTicketsDesc: "For incidents that affect critical systems or cause total service interruption, we offer 24-hour support, 7 days a week, including holidays.",
    responsibilitiesTecit: "TEC-IT Responsibilities",
    responsibilitiesUser: "User Responsibilities",
    respTecit1: "Respond to tickets within the established SLA",
    respTecit2: "Maintain clear and transparent communication",
    respTecit3: "Document all actions taken",
    respTecit4: "Escalate tickets when necessary",
    respTecit5: "Maintain confidentiality of information",
    respTecit6: "Provide performance reports",
    respUser1: "Provide complete and accurate information",
    respUser2: "Respond to test/validation requests",
    respUser3: "Wait for approval before changes",
    respUser4: "Maintain backup of important data",
    respUser5: "Communicate environment changes",
    respUser6: "Rate the service after resolution",
    escalationTitle: "Escalation Process",
    escalationDesc: "How tickets are escalated when necessary",
    escalationAuto: "Automatic Escalation",
    escalationAutoDesc: "When SLA reaches 80% of the deadline",
    escalationTech: "Technical Escalation",
    escalationTechDesc: "To specialists when necessary",
    escalationMgmt: "Management Escalation",
    escalationMgmtDesc: "For supervision in critical cases",
    performanceIndicators: "Performance Indicators",
    mainKpis: "Main KPIs",
    slaCompliance: "SLA Compliance",
    firstResolution: "First Resolution",
    userSatisfaction: "User Satisfaction",
    avgResponseTime: "Average Response Time",
    reportsTitle: "Reports",
    reportItem1: "Monthly performance report",
    reportItem2: "Real-time dashboard",
    reportItem3: "Trend analysis",
    reportItem4: "User feedback",
    reportItem5: "Continuous improvement plans",
    contactFeedbackTitle: "Contact and Feedback",
    contactFeedbackDesc: "Your opinion is essential for us to improve our services",
    contactChannels: "Contact Channels",
    emailLabel: "Email:",
    phoneLabel: "Phone:",
    chatLabel: "Chat:",
    systemLabel: "System:",
    chatAvailable: "Available in the system",
    systemPortal: "ITSM ticket portal",
    complaintsSuggestions: "Complaints and Suggestions",
    complaintsParagraph: "For complaints, suggestions or compliments, contact our quality manager at qualidade@tec-it.com",
    responseTimeNote: "All messages are reviewed and responded to within 48 business hours.",
  },
  documentation: {
    title: "Documentation",
    subtitle: "Registration and database structure.",
    loading: "Loading...",
    loadError: "Could not load documentation.",
  },
  floatingHelp: {
    help: "Help",
    needHelp: "Need help?",
    title: "Help Center",
    description: "Find quick answers and contact our support",
    ariaLabel: "Help and Support",
    humanAvailable: "Human Support Available",
    outsideHours: "Outside Service Hours",
    quickAccess: "Quick Access",
    contactUs: "Contact Us",
    email: "Email",
    phone: "Phone",
    adminPanel: "Admin Panel",
    adminPanelDesc: "Manage system and users",
    adminPanelDescInternal: "Manage company and users",
    service: "Service",
    serviceDesc: "Manage tickets",
    reports: "Reports",
    reportsDesc: "Analytics and metrics",
    manageUsers: "Manage Users",
    manageUsersDesc: "Administer system users",
    manageUsersDescInternal: "Administer company users",
    faq: "FAQ",
    faqDesc: "Frequently asked questions",
    howToOpenTicket: "How to open a ticket",
    howToOpenTicketDesc: "Step-by-step guide",
    servicePolicy: "Service Policy",
    servicePolicyDesc: "Understand our SLAs",
    myTickets: "My Tickets",
    myTicketsDesc: "Track your tickets",
    systemSupport: "System Support",
    systemSupportDesc: "Open a ticket directly with the platform team.",
    titleLabel: "Title *",
    titlePlaceholder: "E.g.: Error creating ticket...",
    descriptionLabel: "Description *",
    descriptionPlaceholder: "Describe the problem and steps to reproduce.",
    impactLabel: "Impact *",
    urgencyLabel: "Urgency *",
    impactCritical: "Critical",
    impactHigh: "High",
    impactMedium: "Medium",
    impactLow: "Low",
    sending: "Sending...",
    sendTicketToSupport: "Send Ticket to Support",
    mySystemTickets: "My system tickets",
    mySystemTicketsDesc: "Track the latest tickets you opened for global support.",
    loadingTickets: "Loading tickets...",
    noTicketsYet: "No tickets opened yet.",
    requiredFields: "Required fields",
    requiredFieldsDesc: "Please provide title and description.",
    ticketSent: "Ticket sent",
    ticketSentDesc: (id: string) => `Ticket ${id} created successfully.`,
    error: "Error",
    errorSendDesc: "Could not open ticket.",
    chatWithAdmin: "Chat with Administrator",
    createNewTicket: "Create New Ticket",
    goToService: "Go to Service",
    needMoreHelp: "Need more help? Our team is ready to assist you.",
  },
  newTicket: {
    title: "New Ticket",
    subtitle: "Fill in the information below to open a new ticket",
    backToTickets: "Back to tickets",
    restrictedAccess: "Restricted access",
    restrictedDesc: "You must be logged in to create a ticket.",
    preselectedService: "Pre-selected service",
    preselectedDesc: (name: string) => `The form was pre-filled with the service information "${name}".`,
    requiredFields: "Required fields",
    requiredFieldsDesc: "Please fill in: What you need, Summary, Description and Problem area.",
    requiredImpactUrgency: "Please select Impact and Urgency.",
    emailRequired: "Email required",
    emailRequiredDesc: "Please enter the email of the person for whom the ticket is.",
    targetCompany: "Target company",
    targetCompanyHint: "The ticket will be created inside this company's database.",
    selectCompany: "Select the company",
    loadingCompanies: "Loading companies...",
    noCompanies: "No company available",
    companyRequired: "Company required",
    companyRequiredDesc: "Select which company the ticket will be created for.",
    invalidEmail: "Invalid email",
    invalidEmailDesc: "Please enter a valid email.",
    outsideHours: "Outside Service Hours",
    outsideHoursDesc: (time: string) => `You are outside service hours (8am to 2am). Your ticket will be created and will be handled when service hours return. Service returns at ${time}.`,
    ticketInfo: "Ticket Information",
    ticketInfoDesc: "Provide as much detail as possible to speed up service",
    whatYouNeed: "What do you need? *",
    select: "Select...",
    resolveProblem: "Resolve a problem",
    requestSomething: "Request something new",
    reportRecurring: "Report a recurring problem",
    makeChange: "Make a change",
    summary: "Problem summary *",
    summaryPlaceholder: "E.g.: I can't open Excel",
    summaryHint: "A short phrase describing the problem",
    tellMore: "Tell us more about the problem *",
    tellMorePlaceholder: "Describe what happened, when it started and what you were doing when the problem appeared. If there is an error message, paste it here.",
    tellMoreHint: "The more details, the faster we can help you",
    areaOfProblem: "Problem area *",
    selectArea: "Select area...",
    areaHint: "Which area is the problem in? Software, Hardware, Access, etc.",
    subcategoryOptional: "Specific type (optional)",
    subcategoryPlaceholder: "If you don't know, leave blank",
    impact: "Impact *",
    urgency: "Urgency *",
    selectImpact: "Select impact...",
    selectUrgency: "Select urgency...",
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
    additionalInfo: "Additional Information",
    additionalInfoHint: "Fill in what you know. If you don't know, you can leave it blank.",
    optional: "(optional)",
    forWho: "This ticket is for *",
    forMyself: "For myself",
    forOther: "For someone else",
    contactEmail: "Contact Email *",
    yourEmailUsed: "(your email will be used)",
    updatesWillBeSent: "Ticket updates will be sent to:",
    contactPhone: "Contact Phone (optional)",
    attachments: "Attachments",
    clickToUpload: "Click to upload or drag files",
    attachmentsHint: "PNG, JPG, PDF (MAX. 10MB) • Paste screenshots with Ctrl+V",
    attachedFiles: "Attached files:",
    remove: "Remove",
    calculatedPriority: "Calculated Priority",
    basedOnImpact: (impact: string, urgency: string) => `Based on impact (${impact}) and urgency (${urgency})`,
    estimatedSLA: "Estimated SLA",
    firstResponseTime: "Time for first response",
    tipsTitle: "Tips for an Efficient Ticket",
    tip1: "Be specific in title and description",
    tip2: "Include screenshots or relevant files",
    tip3: "Say when the problem started",
    tip4: "Describe error messages",
    createTicket: "Open Ticket",
    creating: "Creating ticket...",
    loadingCategories: "Loading categories...",
    printAdded: "Screenshot added",
    ticketCreated: "Ticket created successfully!",
    ticketCreatedDesc: (id: string) => `Your ticket ${id} was created. You will receive updates by email.`,
    ticketLinked: "Ticket created and linked automatically!",
    ticketLinkedDesc: (id: string) => `Your ticket ${id} was linked to an existing ticket with the same problem. You will receive updates by email.`,
    similarTicketsTitle: "We found tickets with the same problem",
    similarTicketsDesc: "Other people have already reported this. Is it the same problem you are facing?",
    createNewAnyway: "No, create new ticket",
    useExisting: "Use existing ticket",
    similarYesSame: "Yes, same one",
    viewMyTicket: "View my ticket",
    printAddedCount: (n: number) => `${n} image(s) added to attachments.`,
    reportedBy: "Reported by:",
    thanksForInfo: "Thank you for the information!",
    teamWillCheck: "The team will check if your ticket is related. If it's the same issue, they will link it automatically.",
    ticketCreatedVerify: "The ticket was created. The team will verify the link.",
    ticketsLinked: "Tickets linked!",
    ticketsLinkedDesc: "Your ticket was linked to the main ticket. When it is resolved, yours will be too.",
    errorCreating: "Error creating ticket",
    errorCreatingDesc: "Could not create the ticket. Please try again.",
    limitReached: "Ticket limit reached",
    configError: "Configuration error",
    configErrorDesc: "The API URL is not configured correctly. Contact technical support.",
    connectionError: "Could not connect to the server. Check your internet connection and try again.",
  },
  ticketDetails: {
    loading: "Loading ticket...",
    notFound: "Ticket not found",
    backToTickets: "Back to tickets",
    createdAt: "Created at",
    updatedAt: "Updated at",
    description: "Description",
    communication: "Communication",
    communicationDesc: "Message history between you and the technical team",
    noMessages: "No messages yet",
    startConversation: "Start the conversation by adding a comment below",
    addCommentPlaceholder: "Add a comment or question...",
    attachImage: "Attach Image (optional, max 10MB)",
    selectImage: "Select Image",
    sendComment: "Send comment",
    error: "Error",
    notFoundDesc: "Ticket not found.",
    loadError: "Could not load the ticket.",
    fileTooBig: "File too large",
    fileTooBigDesc: "The image must be at most 10MB.",
    invalidFileType: "Invalid file type",
    invalidFileTypeDesc: "Only images are allowed.",
    attention: "Attention",
    addCommentOrImage: "Please add a comment or an image.",
    commentAdded: "Comment added",
    commentAddedDesc: "Your message was sent successfully.",
    ratingSent: "Rating sent!",
    ratingSentDesc: "Thank you for your rating. It helps us improve continuously.",
    reopenReasonRequired: "Reason required",
    reopenReasonDesc: "Please provide the reason for reopening. This reason will be reviewed by the team.",
    ticketReopened: "Ticket reopened",
    ticketReopenedDesc: "The ticket was reopened successfully. The reason will be reviewed by the team.",
    rateExperience: "How was your service?",
    npsLabel: "Rate from 0 to 10",
    submitRating: "Submit rating",
    reopenTicket: "Reopen ticket",
    reopenReason: "Reopening reason",
    submitReopen: "Confirm reopening",
    user: "User",
    attachedImage: "Attached image",
    sla: "SLA",
    timeRemaining: "Time Remaining",
    deadline: "Deadline",
    details: "Details",
    type: "Type",
    priority: "Priority",
    category: "Category",
    contactTime: "Contact / Time",
    assignee: "Assignee",
    notAssigned: "Not assigned",
    requester: "Requester",
    notInformed: "Not informed",
    priorityReportedAtOpen: "Priority reported at opening:",
    rateService: "Rate the Service",
    ratingSentTitle: "Rating Sent",
    thanksForRating: "Thank you for your rating!",
    yourOpinionHelps: "Your opinion helps us improve continuously",
    yourNpsRating: "Your NPS Rating",
    scaleZeroToTen: "Scale from 0 to 10",
    yourComment: "Your Comment",
    howLikelyRecommend: "How likely are you to recommend our service? (0-10)",
    promoter: "Promoter",
    neutral: "Neutral",
    detractor: "Detractor",
    promoterDesc: "You are very satisfied!",
    neutralDesc: "Satisfied, but could improve",
    detractorDesc: "Not satisfied",
    wouldYouComment: "Would you like to leave a comment? (optional)",
    commentHint: "Your comments are very important for our training and quality control.",
    commentPlaceholder: "Tell us more about your experience with the service...",
    ratingHelps: "This rating helps us improve our services continuously",
    reopenSectionTitle: "Reopen Ticket",
    reopenSectionDesc: "If the problem was not resolved properly, you can reopen this ticket",
    reopenReasonLabel: "Reopening reason",
    reopenReasonPlaceholder: "Describe in detail why the ticket needs to be reopened. This reason will be reviewed by the team...",
    reopenReasonHint: "The reason is required and will be reviewed by the team before reopening",
    reopenDeadlineHint: "The ticket will be reopened and the reason will be reviewed by the team",
    reopening: "Reopening...",
    sendingComment: "Sending...",
  },
  internalTicketDetails: {
    loading: "Loading ticket...",
    notFound: "Ticket not found",
    backToPanel: "Back to panel",
    description: "Description",
    companyInfo: "Company Information",
    company: "Company",
    requester: "Requester",
    notInformed: "Not informed",
    sla: "SLA",
    slaOverdue: "Expired",
    timeRemaining: "Time Remaining",
    deadline: "Deadline",
    details: "Details",
    type: "Type",
    category: "Category",
    impact: "Impact",
    urgency: "Urgency",
    priority: "Priority",
    priorityWithSlaSuffix: " (SLA / Analyst)",
    customerPriority: "Requester reported priority:",
    contact: "Requester Contact",
    assignee: "Assignee",
    notAssigned: "Not assigned",
    reassign: "Reassign",
    assign: "Assign",
    relatedTickets: "Related Tickets",
    mainTicket: "Main Ticket",
    unlink: "Unlink",
    viewMain: "View main ticket",
    peopleReportedSame: "People who reported the same problem",
    peopleReportedCount: (n: number) => (n === 1 ? "1 person reported this same problem" : `${n} people reported this same problem`),
    noLinkedTickets: "This ticket has no other linked tickets",
    communication: "Communication",
    communicationDesc: "Message and interaction history",
    noMessages: "No messages yet",
    addCommentPlaceholder: "Add a comment...",
    attachImagePaste: "Attach Image (optional, max 10MB) • Paste screenshots with Ctrl+V",
    selectImage: "Select Image",
    attachedImagePreview: "Attached image preview",
    removeImage: "Remove image",
    sending: "Sending...",
    addComment: "Add Comment",
    history: "Change History",
    historyDesc: "Complete record of all changes to the ticket",
    noChanges: "No changes recorded yet",
    reasonLabel: "Reason:",
    changedByLabel: "By:",
    typeIncident: "Incident",
    typeRequest: "Request",
    typeProblem: "Problem",
    typeChange: "Change",
    changeTypeCreated: "Ticket Created",
    changeTypeStatusChange: "Status Change",
    changeTypePriorityChange: "Priority Change",
    changeTypeCategoryChange: "Category Change",
    changeTypeTrackingStatusChange: "Tracking Status Change",
    changeTypeQueueChange: "Queue Change",
    changeTypeAssignment: "Assignment",
    changeTypeTransfer: "Transfer",
    changeTypeLink: "Link",
    changeTypeUnlink: "Unlink",
    changeTypeReopen: "Reopening",
    changeTypeSystemClose: "Closed by System",
    changeTypeCommentAdded: "Comment Added",
    changeTypeAttachmentAdded: "Attachment Added",
    changeTypeNpsRated: "Rating (NPS)",
    changeTypeFieldUpdate: "Field Update",
    parentIdLabel: "Link (Parent Ticket)",
    commentLabel: "Comment",
    attachmentsLabel: "Attachments",
    npsScoreLabel: "NPS",
    trackingPaused: "Paused",
    noLink: "No link",
    attachmentCount: (n: number) => (n === 1 ? "1 attachment" : `${n} attachments`),
    manageTicket: "Manage Ticket",
    manageDesc: "Change status, priority and assign responsible",
    status: "Status",
    priorityLabel: "Priority",
    trackingStatus: "Tracking Status",
    queue: "Service Queue",
    assigneeLabel: "Assignee",
    selectAssignee: "Select an assignee",
    removeAssignment: "Remove Assignment",
    saveChanges: "Save Changes",
    saving: "Saving...",
    quickActions: "Quick Actions",
    quickActionsDesc: "Resolve, close or reopen ticket quickly",
    closureReason: "Closure reason",
    closureReasonPlaceholder: "Explain why the ticket is being closed...",
    closureReasonHint: "This text appears in communication and ticket history.",
    resolveTicket: "Resolve Ticket",
    closeTicket: "Close Ticket",
    reopenTicket: "Reopen Ticket",
    reopenDeadlineExpired: "The deadline to reopen this ticket has expired.",
    transferTicket: "Transfer Ticket",
    transferDesc: "Choose how you want to transfer",
    transferOption: "Transfer Option",
    transferToTechnician: "Transfer to another technician",
    transferQueueOnly: "Transfer queue only (keep assignee)",
    newAssignee: "New Assignee",
    destinationQueue: "Destination Queue",
    optionalQueue: "Destination Queue (optional)",
    optionalQueuePlaceholder: "Automatic (technician's preferred queue)",
    optionalQueueHint: "If not set, queue will be set by the technician's preferred queue.",
    transferReason: "Transfer Reason",
    transferReasonPlaceholder: "Explain the reason for the transfer...",
    transferBtn: "Transfer Ticket",
    queueChangeHint: "Changing the queue moves the ticket to another level",
    reopenTitle: "Reopen Ticket",
    reopenDesc: "Reopen ticket if the issue was not resolved properly",
    reopenDialogDesc: (id: string | number) => `Provide the reason for reopening ticket #${id}`,
    reopenReasonLabel: "Reopening Reason",
    reopenReasonPlaceholder: "Explain why the ticket needs to be reopened...",
    reopenDeadline: "Reopening deadline:",
    cancel: "Cancel",
    confirm: "Confirm",
    reassignTicket: "Reassign Ticket",
    assignTicket: "Assign Ticket",
    selectForTicket: "Select assignee for ticket",
    ticketTitle: "Ticket Title",
    currentAssignee: "Current Assignee",
    assignTo: "Assign to",
    notAssigned: "Not assigned",
    printAttached: "Screenshot attached",
    printAttachedDesc: "Image pasted and added to comment.",
    error: "Error",
    loadError: "Could not load ticket data.",
    commentAdded: "Comment added",
    ticketUpdated: "Ticket updated",
    noChangesToSave: "No changes",
    noChangesToSaveDesc: "There are no changes to save.",
    updateError: "Could not update ticket.",
    transferSuccess: "Ticket transferred",
    assignSuccess: "Ticket assigned",
    assignmentRemoved: "Assignment removed",
    assignmentRemovedDesc: "The ticket no longer has an assignee.",
    reopenSuccess: "Ticket reopened",
    resolveSuccess: "Ticket resolved",
    closeSuccess: "Ticket closed",
    unlinkSuccess: "Ticket unlinked",
    slaExpired: "SLA Expired",
    statusOpen: "Open",
    statusInProgress: "In Progress",
    statusWaiting: "Waiting Response",
    statusResolved: "Resolved",
    statusClosed: "Closed",
    priorityCritical: "Critical",
    priorityHigh: "High",
    priorityMedium: "Medium",
    priorityLow: "Low",
    queueN1: "N1 - First Level",
    queueN2: "N2 - Second Level",
    queueN3: "N3 - Specialized",
    trackingMonitoring: "Monitoring",
    trackingWaiting: "Waiting",
    trackingResolved: "Resolved",
    trackingEscalated: "Escalated",
    loadingTicketInfo: "Loading ticket information...",
    categoryInfra: "Infrastructure",
    categoryHardware: "Hardware",
    categorySoftware: "Software",
    categoryAccess: "Access and Permissions",
    categoryPerformance: "Performance",
    roleAdmin: "Admin",
    roleAnalyst: "Analyst",
    roleAttendant: "Attendant",
    meShort: "Me",
    noName: "No name",
    noEmail: "No email",
    actions: "Actions",
    reasonRequired: "Reason required",
    reasonRequiredDesc: "Provide the closure reason to resolve the ticket.",
    selectTechnician: "Select technician",
    selectQueue: "Select queue",
    noAssigneeTransfer: "This ticket has no assignee. Select a technician to transfer.",
    noAssigneeQueue: "This ticket has no assignee. To change queue, assign a technician first.",
    pleaseProvideReopen: "Please provide the reopening reason.",
  },
  serviceCatalog: {
    title: "Service Catalog",
    subtitle: "Choose a service to open a ticket",
    subtitleAlt: "Discover available services and their response times",
    searchPlaceholder: "Search services...",
    openTicket: "Open Ticket",
    noServices: "No services found",
    noServicesDesc: "Try adjusting the filters or search.",
    allCategories: "All categories",
    all: "All",
    addService: "Add Service",
    loading: "Loading...",
    noServicesFilter: "No services found with the selected filters.",
    category: "Category:",
    sla: "SLA:",
    estimatedTime: "Estimated Time:",
    requestService: "Request Service",
    addServiceTitle: "Add Service to Catalog",
    addServiceDesc: "Administrators can add new services for the company.",
    name: "Name *",
    namePlaceholder: "E.g.: Request Access to System X",
    description: "Description *",
    descriptionPlaceholder: "Explain what this service covers...",
    categoryLabel: "Category *",
    categoryPlaceholder: "E.g.: Infrastructure",
    type: "Type *",
    select: "Select",
    incident: "Incident",
    request: "Request",
    problem: "Problem",
    change: "Change",
    slaOptional: "SLA (optional)",
    slaPlaceholder: "E.g.: 24h - 48h",
    estimatedTimeOptional: "Estimated Time (optional)",
    estimatedTimePlaceholder: "E.g.: 1-2 days",
    company: "Company *",
    selectCompany: "Select company",
    companyRequiredHint: "For global admin (no company), selection is required.",
    cancel: "Cancel",
    save: "Save",
    requiredFields: "Required fields",
    requiredFieldsDesc: "Fill in Name, Description, Category and Type.",
    companyRequired: "Company required",
    companyRequiredDesc: "Select the company to add the service.",
    serviceAdded: "Service added",
    serviceAddedDesc: "The item now appears in the Catalog.",
    error: "Error",
    errorLoad: "Could not load catalog.",
    errorRemove: "Error removing service.",
    errorAdd: "Error adding service.",
    removeConfirm: "Do you want to remove this service from the catalog?",
    removed: "Removed",
    removedDesc: "Service removed from catalog.",
    deleteBtn: "Delete",
    removeTitle: "Remove from catalog",
    selectPlaceholder: "Select...",
    default1Name: "Infrastructure Support",
    default1Desc: "Support for server, network, email and connectivity issues",
    default1Category: "Infrastructure",
    default2Name: "Hardware Support",
    default2Desc: "Repair and maintenance of computers, notebooks, printers and peripherals",
    default2Category: "Hardware",
    default3Name: "Software Support",
    default3Desc: "Installation, configuration and troubleshooting of applications",
    default3Category: "Software",
    default4Name: "Access Request",
    default4Desc: "User creation, permissions and system access",
    default4Category: "Access and Permissions",
    default5Name: "Performance Analysis",
    default5Desc: "Investigation of slowness issues and system optimization",
    default5Category: "Performance",
    default6Name: "Infrastructure Changes",
    default6Desc: "Updates, migrations and changes to servers and network",
    default6Category: "Infrastructure",
  },
  reports: {
    title: "Reports",
    subtitle: "Generate ticket reports by period",
    subtitleAlt: "Generate detailed reports on ticket performance",
    generateReport: "Generate Report",
    generating: "Generating...",
    selectDates: "Select start and end dates",
    reportGenerated: "Report generated",
    reportGeneratedDesc: "The report was generated successfully.",
    error: "Error",
    errorGenerating: "Could not generate the report.",
    generateFirst: "Generate a report first",
    exportPdf: "Export PDF",
    exportCsv: "Export CSV",
    exportExcel: "Export Excel",
    period: "Period",
    startDate: "Start date",
    endDate: "End date",
    reportType: "Report Type",
    summary: "Summary",
    configureReport: "Configure Report",
    configureDesc: "Select the report type and desired period",
    summaryExecutive: "Executive Summary",
    detailed: "Detailed",
    slaAnalysis: "SLA Analysis",
    byTechnician: "By Technician",
    byCategory: "By Category",
    exportSuccess: "Exported successfully",
    exportCsvDesc: "CSV file downloaded",
    exportExcelDesc: "Excel file downloaded",
    allowPopups: "Allow pop-ups to export PDF",
    openingPrint: "Opening print",
    openingPrintDesc: "Use 'Save as PDF' in the print window",
    reportMonthlyPdf: "Monthly Report (PDF)",
    performanceCsv: "Performance Data (CSV)",
    satisfactionExcel: "User Satisfaction (Excel)",
    exportReports: "Export Reports",
    exportReportsDesc: "Generate detailed reports for analysis",
    ticketsByCategory: "Tickets by Category",
    slaMetrics: "SLA Metrics",
    slaCompliance: "SLA Compliance",
    averageResolutionTime: "Average Resolution Time",
    averageResponseTime: "Average Response Time",
    firstResolution: "First Resolution",
    reportTitle: "Ticket Report",
    periodLabel: "Period",
    metric: "Metric",
    value: "Value",
    totalTickets: "Total Tickets",
    backlog: "Backlog",
    backlogOverdue: "Backlog Overdue",
    backlogSlaBreached: "Backlog SLA Breached",
    backlogTransferred: "Backlog Transferred",
    totalTransferred: "Total Transferred",
    multipleTransfers: "Multiple Transfers",
    open: "Open",
    resolved: "Resolved",
    closed: "Closed",
    reopenRate: "Reopen Rate",
    npsAverage: "Average NPS",
    category: "Category",
    quantity: "Quantity",
    ticketsByTechnician: "Tickets by Technician",
    technician: "Technician",
    noDataInPeriod: "No data in period",
    slaComplianceShort: "SLA Compliance",
    violations: "Violations",
    backlogByAge: "Backlog by Age",
    age: "Age",
    metOfTotal: (met: number, total: number) => `${met} of ${total} met`,
    overdueTickets: "Overdue Tickets",
    daysOverdue: (n: number) => `${n} days overdue`,
    performanceByTechnician: "Performance by Technician",
    ticketsCount: (n: number) => `${n} tickets`,
    averageTime: "Average Time",
    categoryAnalysis: "Analysis by Category",
    percentOfTotal: (p: number) => `${p}% of total`,
    noTechnicianFound: "No technician found in period",
    noCategoryFound: "No category found in period",
    periodSummary: "Period Summary",
    backlogSlaViolations: "Backlog - SLA Violations",
    overdueOpen: "Overdue (open)",
    passedSla: "SLA Breached (total)",
    violationsInPeriod: "Violations in period",
    transferredTicketsCard: "Transferred Tickets",
    totalTransferredShort: "Total transferred",
    backlogTransferredShort: "Backlog transferred",
    multipleTransfersShort: "Multiple transfers",
    transferredAnalysis: "Transferred Tickets Analysis",
    transferredStatsDesc: "Statistics on tickets transferred between technicians",
    distribution: "Distribution",
    transferredAndResolved: "Transferred and resolved",
    transferredStillOpen: "Transferred still open",
    multipleTransfersRate: "Multiple transfer rate",
    ofTotal: (p: number) => `${p}% of total`,
    ofBacklog: (p: number) => `${p}% of backlog`,
    transferredMoreThanOnce: "Tickets transferred more than once",
    slaQuality: "SLA Compliance",
    violationsInPeriodShort: (n: number) => `${n} violations in period`,
    slaByCategory: "SLA by Category",
    metOfTotalDetail: (met: number, withSla: number, total: number) => `${met} of ${withSla} met (${total} total)`,
    generatedAt: "Generated at",
    inOverdue: "overdue",
    slaBreachedShort: "SLA breached",
    transferredShort: "transferred",
  },
  adminDashboard: {
    title: "Admin Dashboard",
    subtitle: "Ticket, company and user management",
    subtitleAlt: "Overview of technical team and ITSM performance",
    tickets: "Tickets",
    companies: "Companies",
    users: "Users",
    loading: "Loading...",
    loadingData: "Loading data...",
    totalTickets: "Total Tickets",
    queueTab: "Service Queue",
    technicians: "Technicians",
    settings: "Settings",
    queueTitle: "Ticket Queue",
    queueDesc: "Manage assignments and priorities",
    averageTime: "Average Time",
    satisfaction: "Satisfaction",
    total: "Total",
    results: "Results",
    active: "Active",
    inactive: "Inactive",
    activePlural: "Active",
    inactivePlural: "Inactive",
    activeMale: "Active",
    inactiveMale: "Inactive",
    edit: "Edit",
    deactivate: "Deactivate",
    remove: "Remove",
    filterByStatus: "Filter by status",
    filterByProfile: "Filter by profile",
    filterByCompany: "Filter by company",
    all: "All",
    allProfiles: "All Profiles",
    allStatuses: "All Statuses",
    allCompanies: "All Companies",
    noCompany: "No Company",
    companyManagement: "Company Management",
    companyManagementDesc: "Register and manage user companies in the system",
    searchCompanyPlaceholder: "Search by name, domain or phone...",
    newCompany: "New Company",
    downloadTemplate: "Download template",
    importCsv: "Import CSV",
    ticketsUnlimited: "Unlimited",
    ticketsCount: (used: number, limit: number) => `Tickets: ${used}/${limit}`,
    peopleCount: (n: number) => (n === 1 ? "1 person registered" : `${n} people registered`),
    userManagement: "User Management",
    userManagementDesc: "View, filter and manage all users in the system",
    importUsers: "Import Users",
    addUser: "Add User",
    searchUserPlaceholder: "Search by name or email...",
    company: "Company",
    domain: "Domain",
    resetPassword: "Reset Password",
    delete: "Delete",
    manageCategories: "Manage Categories",
    manageCategoriesDesc: "Add or edit ticket categories",
    ticketsCountLabel: (n: number) => (n === 1 ? "1 ticket" : `${n} tickets`),
    categoryTicketsCount: (n: number) => (n === 1 ? "(1 ticket)" : `(${n} tickets)`),
    noCategoriesFound: "No categories found. Add a new category to get started.",
    addNewCategory: "Add New Category",
    addCategoryDialogTitle: "Add New Category",
    addCategoryDialogDesc: "Create a new ticket category. The category will be available when creating new tickets.",
    categoryNameLabel: "Category Name",
    categoryNamePlaceholder: "E.g.: IT Security",
    categoryNameHint: "Enter the name of the category you want to add",
    add: "Add",
    editCategoryDialogTitle: "Edit Category",
    editCategoryDialogDesc: (name: string) => `Rename the category "${name}". All tickets with this category will be updated.`,
    newCategoryNameLabel: "New Category Name",
    newCategoryNamePlaceholder: "Enter the new name",
    removeCategoryDialogTitle: "Remove Category",
    removeCategory: "Remove Category",
    exportNps: "Export NPS Ratings",
    exportNpsDesc: "Export all NPS ratings with comments for training and quality control",
    training: "Training",
    qualityControl: "Quality Control",
    xlsxRecommended: "XLSX recommended",
    exportIncludes: "The export includes: NPS score (0-10), comments, ticket data, client, responsible technician, company and relevant dates.",
    format: "Format",
    selectFormat: "Select format",
    xlsxRecommendedOption: "XLSX (recommended)",
    exporting: "Exporting...",
    exportXlsx: "Export XLSX",
    exportXls: "Export XLS",
    exportCsv: "Export CSV",
    xlsxKeepsFields: "XLSX keeps each field in its own cell.",
    newCompanyDialogTitle: "New Company",
    editCompanyDialogTitle: "Edit Company",
    editUserDialogTitle: "Edit User",
    addUserDialogTitle: "Add User",
    cancel: "Cancel",
    saveChanges: "Save Changes",
    activate: "Activate",
    noUsersFound: "No users found with the applied filters",
    noCompaniesFound: "No companies found",
    createFirstCompany: "Create First Company",
    importCompanies: "Import Companies",
    newCompanyDialogDesc: "Register a new user company in the system",
    editCompanyDialogDesc: "Update the company information as needed",
    editUserDialogDesc: "Update the user information as needed.",
    addUserDialogDesc: "Create a new analyst and associate them with the correct company.",
    importUsersDialogDesc: "Download the template, fill it in and upload the spreadsheet. Normal registration fields: name, email, phone, company_domain, department, role.",
    importCompaniesDialogDesc: "Download the template, fill it in and upload the CSV. Required fields: name and domain. The rest follow normal registration.",
    noFileChosen: "No file chosen",
    importing: "Importing...",
    saving: "Saving...",
    sendCsv: "Send CSV",
    internalUser: "Internal user",
    removeCategoryDialogDesc: (name: string) => `Are you sure you want to remove the category "${name}"?`,
    selectTargetCategoryPlaceholder: "Select target category",
    roleAdminInternal: "Internal Admin",
    roleAdministrator: "Administrator",
    roleAnalyst: "Analyst",
    roleUser: "User",
    downloadTemplateReady: "Download template",
    chooseFile: "Choose file",
    spreadsheet: "Spreadsheet",
    import: "Import",
    nameLabel: "Name",
    emailLabel: "E-mail",
    fullNamePlaceholder: "Full name",
    filterByQueue: "Filter by queue",
    filterByTechnician: "Filter by technician",
    filterByLevel: "Support level",
    allQueues: "All Queues",
    allTechnicians: "All Technicians",
    allLevels: "All Levels",
    queueInfra: "Infrastructure",
    queueHardware: "Hardware",
    queueSoftware: "Software",
    queueSecurity: "IT Security",
    levelN1: "N1 - First Level",
    levelN2: "N2 - Second Level",
    levelN3: "N3 - Specialized",
    slaExpiredBadge: "SLA EXPIRED",
    transferCount: (n: number) => `${n} transfer(s)`,
    categoryLabel: "Category",
    requesterLabel: "Requester",
    slaLabel: "SLA",
    assigneeLabel: "Assignee",
    notAssigned: "Unassigned",
    assign: "Assign",
    reassign: "Reassign",
    urgentAction: "Urgent action required",
    clickToOpen: "Click to open ticket",
    noTicketsQueue: "No tickets found. Assignment buttons will appear here when there are tickets.",
    noTicketsFiltered: "No tickets found with the applied filters. Adjust the filters above.",
    techniciansPerformance: "Technician Performance",
    techniciansPerformanceDesc: "Workload distribution and productivity",
    resolved: "resolved",
    resolutionRate: "Resolution rate",
    ticketTitle: "Ticket Title",
    currentAssignee: "Current Assignee",
    assignTo: "Assign to",
    loadingUsers: "Loading users...",
    selectAssignee: "Select an assignee",
    me: "(Me)",
    confirm: "Confirm",
    assignTicketTitle: "Assign Ticket",
    reassignTicketTitle: "Reassign Ticket",
    assignTicketDialogDesc: (id: string | number) => `Select the assignee for ticket ${id}`,
    // Settings
    brandingTitle: "Company Visual Identity",
    brandingDesc: (company: string) => `Customize logo and colors for ${company} portal.`,
    brandingYourCompany: "your company",
    loadingBranding: "Loading branding...",
    lastSavedConfig: "Last saved configuration",
    logoSaved: "Saved logo",
    noLogoSaved: "No logo saved.",
    primaryColor: "Primary",
    secondaryColor: "Secondary",
    companyLogo: "Company Logo",
    uploadLogoHint: "Upload a PNG/JPG file (recommended max 1MB).",
    primaryColorLabel: "Primary Color",
    secondaryColorLabel: "Secondary Color",
    preview: "Preview",
    saveBranding: "Save Visual Identity",
    brandingUpdated: "Branding updated",
    brandingUpdatedDesc: "Colors and logo have been saved successfully.",
    logoUploaded: "Logo uploaded",
    logoUploadedDesc: "Logo updated successfully.",
    brandingSaveError: "Could not save branding.",
    logoUploadError: "Could not upload logo.",
    errorTitle: "Error",
    categoryAdded: "Category added",
    categoryUpdated: "Category updated",
    categoryRemoved: "Category removed",
    categoryDisplayName: (name: string) => {
      const map: Record<string, string> = {
        "Acesso e Permissões": "Access and Permissions",
        "Infraestrutura": "Infrastructure",
        "Performace": "Performance",
        "Performance": "Performance",
        "Hardware": "Hardware",
        "Software": "Software",
      };
      return map[name] ?? name;
    },
  },
};
